<template>
  <transition name="sidebar-wrapper">
    <div v-if="isOpen" class="nav-sidebar-wrapper">
      <div class="nav-backdrop" @click="$emit('close')"></div>

      <div
        class="nav-sidebar"
        :class="{ 'nav-sidebar--fullscreen': isFullscreen }"
        @click.stop
      >
        <div class="sidebar-header">
          <h2 class="sidebar-title">Navigation</h2>
          <button class="close-button" @click="$emit('close')" title="Close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="sidebar-content">
          <button
            v-for="action in filteredActions"
            :key="action.id + '-' + action.label"
            class="nav-item"
            :class="{ 'nav-item--active': isActive(action) }"
            @click="handleClick(action)"
          >
            <i class="nav-item-icon" :class="['fas', action.icon]"></i>
            <span class="nav-item-label">{{ action.label }}</span>
            <span
              v-if="isActive(action)"
              class="nav-item-dot"
              aria-hidden="true"
            ></span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState } from "vuex";
import { quickActions } from "@/components/OliverBotV2/suggestions/suggestionsData";
import { USER_ROLES } from "@/components/OliverBotV2/suggestions/constants";

export default {
  name: "NavigationSidebar",
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    isFullscreen: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
    }),
    userRole() {
      if (!this.user) return USER_ROLES.PROVIDER;
      if (this.user.isAdmin) return USER_ROLES.ADMIN;
      if (this.user.isManagingAdmin) return USER_ROLES.MANAGER;
      return USER_ROLES.PROVIDER;
    },
    filteredActions() {
      return quickActions.filter((action) => {
        if (!action.roles || action.roles.length === 0) return true;
        return action.roles.includes(this.userRole);
      });
    },
  },
  methods: {
    isActive(action) {
      return action.route && this.$route.path === action.route;
    },
    handleClick(action) {
      if (action.route && this.$route.path !== action.route) {
        this.$router.push(action.route);
      }
      this.$emit("navigate", action);
    },
  },
};
</script>

<style lang="scss" scoped>
.sidebar-wrapper-enter-active,
.sidebar-wrapper-leave-active {
  transition: opacity 0.3s ease;

  .nav-backdrop {
    transition: opacity 0.3s ease;
  }

  .nav-sidebar {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

.sidebar-wrapper-leave-active {
  .nav-sidebar {
    transition: transform 0.3s cubic-bezier(0.4, 0, 1, 1);
  }
}

.sidebar-wrapper-enter-from,
.sidebar-wrapper-leave-to {
  .nav-backdrop {
    opacity: 0;
  }

  .nav-sidebar {
    transform: translateX(-100%);
  }
}

.nav-sidebar-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  pointer-events: none;

  & > * {
    pointer-events: all;
  }
}

.nav-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(2px);
  cursor: pointer;
}

.nav-sidebar {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 320px;
  background: var(--oliver-bg, var(--body-bg-color, #fff));
  box-shadow: 2px 0 16px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;

  &--fullscreen {
    width: 380px;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 360px;
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 100%;
  }
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.06));
  flex-shrink: 0;
}

.sidebar-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--oliver-text);
  margin: 0;
}

.close-button {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--oliver-btn-subtle-hover, rgba(0, 0, 0, 0.06));
  border: none;
  color: var(--oliver-accent);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 13px;
  }
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 14px;
  background: none;
  border: none;
  text-align: left;
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  cursor: pointer;
  color: var(--oliver-text);
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s ease;
  position: relative;

  &:hover {
    background: var(--oliver-btn-subtle-hover, rgba(0, 0, 0, 0.06));
  }

  &--active {
    background: var(--oliver-btn-subtle-hover, rgba(0, 0, 0, 0.06));
    color: var(--oliver-accent);
    font-weight: 600;
    border-left: 3px solid var(--oliver-accent);
    padding-left: 11px;
  }
}

.nav-item-icon {
  width: 18px;
  font-size: 14px;
  color: var(--oliver-text);
  text-align: center;
}

.nav-item-label {
  flex: 1;
}

.nav-item-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--oliver-accent);
  opacity: 0.7;
}
</style>
